import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import HeaderCard from '../../../components/Header/HeaderCard';
import TableList from '../../../components/Tools/TableList';
import { apiService } from '../../../components/Config/api.service';

function Contracts() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Utilisation d'un tableau pour searchQuery pour supporter les mots-clés multiples de TableList
    const [searchQuery, setSearchQuery] = useState([]);
    const [filterBy, setFilterBy] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total_pages: 1,
        total_count: 0
    });
    
    const columns = [
        { key: 'Name', label: t('contractsList.reference'), sortable: true },
        {
            key: 'Employee',
            label: t('contractsList.employee'),
            sortable: true,
            // Si vous avez des images d'employés dans les données, vous pouvez activer l'avatar
            avatar: true
        },
        { key: 'Job Position', label: t('contractsList.jobPosition'), sortable: true },
        { key: 'Start Date', label: t('contractsList.startDate'), sortable: true },
        { key: 'End Date', label: t('contractsList.endDate'), sortable: true },
        {
            key: 'Statut',
            label: t('contractsList.status'),
            sortable: true,
            render: (row) => {
                let badgeClass = '';
                let statusText = '';

                if (row.expiration_pending) {
                    badgeClass = 'badge-warning';
                    statusText = t('contractsList.statusExpiringSoon');
                } else {
                    // Normalisation pour éviter les erreurs de casse
                    const status = row.Statut ? row.Statut.toLowerCase() : '';

                    switch(status) {
                        case 'open':
                        case 'running':
                            badgeClass = 'badge-success';
                            statusText = t('contractsList.statusRunning');
                            break;
                        case 'close':
                        case 'expired':
                        case 'cancel':
                            badgeClass = 'badge-error';
                            statusText = t('contractsList.statusExpired');
                            break;
                        case 'draft':
                        case 'new':
                            badgeClass = 'badge-info';
                            statusText = t('contractsList.statusNew');
                            break;
                        default:
                            badgeClass = 'badge-neutral';
                            statusText = row.Statut || 'N/A';
                    }
                }

                return <span className={`badge ${badgeClass} text-white border-none`}>{statusText}</span>;
            }
        },
    ];
    
    const normalizeFilterBy = (filter = {}) => ({
        ...filter,
        filters: Array.isArray(filter?.filters) ? filter.filters : [],
    });

    const hasActiveFilters = (filter = {}) => {
        const normalized = normalizeFilterBy(filter);
        return normalized.filters.length > 0;
    };
    
    const fetchContracts = (page = 1, search = [], filter = null) => {
        setLoading(true);
        
        const offset = (page - 1) * pagination.limit;
        
        const params = new URLSearchParams();
        params.append('offset', offset);
        params.append('limit', pagination.limit);
        
        // Gestion de la recherche (TableList renvoie un tableau ou une chaîne)
        const term = Array.isArray(search) ? search.join(' ') : search;
        if (term) {
            params.append('search', term);
        }

        // Si votre backend supporte le filtrage avancé via JSON ou domain
        if (filter) {
            params.append('filter_by', JSON.stringify(filter));
        }
        
        apiService.getElement(`/contracts?${params.toString()}`)
            .then(response => {
                // Vérification défensive de la structure des données
                const contractsData = response.data.contracts || response.data || [];
                setData(contractsData);

                const responseData = response.data;
                if (responseData.pagination) {
                    // Calcul safe de la page courante
                    const safeLimit = responseData.pagination.limit || 10;
                    const currentPage = Math.floor((responseData.pagination.offset || 0) / safeLimit) + 1;
                    
                    setPagination({
                        ...responseData.pagination,
                        page: currentPage 
                    });
                }
                setLoading(false);
            })
            .catch(error => {
                console.error('There was an error fetching the data!', error);
                setLoading(false);
            });
    };

    // Chargement initial
    useEffect(() => {
        fetchContracts(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getEmployeeIdFromRow = (row) => {
        if (!row) return null;
        const candidate = row.employee_id ?? row.Employee_id ?? row.employee ?? row.Employee;
        if (Array.isArray(candidate)) return candidate[0];
        if (typeof candidate === 'string' && /^\d+$/.test(candidate)) return Number(candidate);
        return candidate;
    };

    const handleContractRowClick = (row) => {
        const employeeId = getEmployeeIdFromRow(row);
        if (employeeId) {
            navigate(`/employees/${employeeId}?tab=Contrats`);
        } else {
            console.warn('Impossible de déterminer l\'employé pour ce contrat', row);
        }
    };

    // Handler unique qui centralise tous les changements venant du tableau (page, recherche, filtres)
    const handleTableChange = (page, search, filter) => {
        setSearchQuery(search);
        setFilterBy(filter);
        fetchContracts(page, search, filter);
    };

    // Gestion de la sélection des lignes
    const handleSelectedRowsChange = (ids) => {
        setSelectedIds(ids);
    };

    return (
        <div className="min-h-screen bg-gray-50/50 pb-8">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="pt-6">  
                    <HeaderCard
                        title={t('contractsList.title')}
                        text={t('contractsList.subtitle')}
                        exportProps={{
                            model_name: 'contract',
                            domain: [],
                            ids: selectedIds,
                            importUrl: '/import/contract',
                            onImportSuccess: () => fetchContracts(pagination.page, searchQuery, filterBy),
                        }}
                        goBack={() => window.history.back()}
                    />
                </div>
                
                <div className="mt-6">
                    <TableList 
                        columns={columns} 
                        data={data} 
                        loading={loading}
                        // Props de pagination et données
                        pagination={pagination}
                        onPageChange={handleTableChange}
                        
                        // Props de recherche et filtrage activées
                        searchDisplay={true}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        
                        // Activation des filtres avancés
                        showFilter={true}
                        filterBy={filterBy}
                        setFilterBy={setFilterBy}
                        model="contract" // Permet à TableList de récupérer les champs disponibles pour le filtrage
                        
                        // Navigation
                        onRowClick={handleContractRowClick}
                        
                        // Actions supplémentaires si nécessaire
                        selectable={true}
                        onSelectedRowsChange={handleSelectedRowsChange}
                        selectActions={(ids) => (
                            <button className="btn btn-sm btn-error text-white ml-2">
                                {t('contractsList.deleteSelected', { count: ids.length })}
                            </button>
                        )}
                    />
                </div>
            </div>
        </div>
    );
}

export default Contracts;